import { DateTimeFormat } from '@billy/design-system'
import { TFunction } from 'i18next'
import { z, ZodType } from 'zod'

import { ContactType } from '@features/contacts/types/contactType'
import { INVOICE_FOOTER_CHARACTER_LIMIT } from '@features/invoices/constants/invoiceFooterCharacterLimit'
import { INVOICE_MESSAGE_CHARACTER_LIMIT } from '@features/invoices/constants/invoiceMessageCharacterLimit'
import { MINIMAL_INVOICE_NUMBER } from '@features/invoices/constants/minimalInvoiceNumber'
import { DepositInvoiceAmountType } from '@features/invoices/enums/depositInvoiceAmountType'
import { InvoiceDiscountType } from '@features/invoices/enums/invoiceDiscountType'
import { InvoiceLineType } from '@features/invoices/enums/invoiceLineType'
import { ZervantLocale } from '@features/invoices/enums/zervantLocale'
import { InvoiceFormDepositLine } from '@features/invoices/types/invoiceForm/invoiceFormLine/invoiceFormDepositLine'
import { InvoiceFormDescriptionLine } from '@features/invoices/types/invoiceForm/invoiceFormLine/invoiceFormDescriptionLine'
import { InvoiceFormRowLine } from '@features/invoices/types/invoiceForm/invoiceFormLine/invoiceFormRowLine'
import { InvoiceFormSubtotalLine } from '@features/invoices/types/invoiceForm/invoiceFormLine/invoiceFormSubtotalLine'
import { getInvoiceSummaryTotals } from '@features/invoices/utils/getInvoiceSummaryTotals'
import { getTotalAmount } from '@features/invoices/utils/getTotalAmount'
import { hasInvoiceLineDiscount } from '@features/invoices/utils/hasInvoiceLineDiscount'
import { isInvoiceFormRowLine } from '@features/invoices/utils/isInvoiceFormRowLine'
import { ProductCategory } from '@features/products/enums/productCategory'
import { ProductPricingModel } from '@features/products/enums/productPricingModel'
import { ProductType } from '@features/products/enums/productType'

import { formatDateToString } from '../../../../../../utils/formatDateToString'
import { getCurrentLocale } from '../../../../../../utils/getCurrentLocale'
import {
  INVOICE_CLIENT_REFERENCE_CHARACTER_LIMIT,
  INVOICE_PREFIX_CHARACTER_LIMIT,
  MAXIMAL_INVOICE_NUMBER,
} from '../../constants'
import { InvoiceFormSchema } from '../../types/invoiceFormSchema'

const getInvoiceRowLineSchema = (t: TFunction, isCreditNote: boolean): ZodType<InvoiceFormRowLine> => {
  return z
    .object({
      date: z.date({
        message: t('required_field'),
      }),
      discount: z.number().min(0),
      productName: z.string().trim(),
      productFromSearch: z
        .object({
          category: z.nativeEnum(ProductCategory),
          id: z.string(),
          name: z.string(),
          price: z.number(),
          pricingModel: z.nativeEnum(ProductPricingModel),
          productType: z.nativeEnum(ProductType),
          total: z.number(),
          unit: z.string(),
          vatPct: z.number(),
          vatRateId: z.string().optional(),
        })
        .optional()
        .nullable(),
      productId: z.string().optional().nullable(),
      productType: z.nativeEnum(ProductType),
      quantity: z.number(),
      totalExcludingVat: z.number(),
      totalIncludingVat: z.number(),
      type: z.literal(InvoiceLineType.Row),
      unit: z.string(),
      unitPrice: z.number(),
      vatRateId: z.string(),
      vatRatePercent: z.number(),
    })
    .refine(
      (line) => {
        if (!line.productFromSearch && !line.productName) {
          return false
        }

        return true
      },
      {
        message: t('external_invoices.editor.form.field.line.field.product_name.error.required'),
        path: ['productFromSearch'],
      },
    )
    .refine((line) => !isCreditNote || line.unitPrice > 0, {
      message: t('external_invoices.editor.form.field.line.field.unit_price.error.must_be_positive'),
      path: ['unitPrice'],
    })
}

const getInvoiceDepositLineSchema = (): ZodType<InvoiceFormDepositLine> => {
  return z.object({
    depositValue: z.number(),
    depositValueType: z.nativeEnum(DepositInvoiceAmountType),
    description: z.string(),
    totalExcludingVat: z.number(),
    totalIncludingVat: z.number(),
    type: z.literal(InvoiceLineType.Deposit),
    vatRateId: z.string(),
    vatRatePercent: z.number(),
  })
}

const getInvoiceDescriptionLineSchema = (): ZodType<InvoiceFormDescriptionLine> => {
  return z.object({
    content: z.string(),
    type: z.literal(InvoiceLineType.Description),
  })
}

const getInvoiceSubtotalLineSchema = (): ZodType<InvoiceFormSubtotalLine> => {
  return z.object({
    subtotalExcludingVat: z.number(),
    subtotalIncludingVat: z.number(),
    type: z.literal(InvoiceLineType.Subtotal),
  })
}

interface GetInvoiceFormSchemaOptions {
  fiscalLockingDate?: Date
  isCreditNote: boolean
  isQuote?: boolean
  isRecurringInvoice?: boolean
  latestInvoiceIssueDate?: Date
  maxIssueDate?: Date
  t: TFunction
}

export const getInvoiceFormSchema = ({
  fiscalLockingDate,
  isCreditNote,
  isQuote,
  isRecurringInvoice,
  latestInvoiceIssueDate,
  t,
}: GetInvoiceFormSchemaOptions): InvoiceFormSchema => {
  return z
    .object({
      buyerReference: z.string().max(INVOICE_CLIENT_REFERENCE_CHARACTER_LIMIT).optional(),
      currency: z.string(),
      dateOfSaleOrService:
        isQuote || isCreditNote || isRecurringInvoice ? z.date().nullable() : z.date({ message: t('required_field') }),
      discount: z.number().min(0),
      discountType: z.nativeEnum(InvoiceDiscountType),
      displayedPaymentMethods: z.object({
        bank: z.boolean().optional(),
        offlinePayment: z.boolean().optional(),
        paypal: z.boolean().optional(),
        stripeCardPayment: z.boolean().optional(),
      }),
      dueDate: z.date({
        message: t('required_field'),
      }),
      footerNote: z.string().max(INVOICE_FOOTER_CHARACTER_LIMIT),
      invoiceLines: z.array(
        z.union([
          getInvoiceRowLineSchema(t, isCreditNote),
          getInvoiceDescriptionLineSchema(),
          getInvoiceSubtotalLineSchema(),
          getInvoiceDepositLineSchema(),
        ]),
      ),
      invoiceNumber: z
        .number()
        .min(MINIMAL_INVOICE_NUMBER)
        .max(MAXIMAL_INVOICE_NUMBER, {
          message: t('external_invoices.editor.form.field.invoice_number.error.max_number'),
        }),
      invoiceNumberPrefix: z
        .string()
        .max(INVOICE_PREFIX_CHARACTER_LIMIT, {
          message: t('external_invoices.editor.form.field.invoice_prefix.error.max_length'),
        })
        .optional(),
      issueDate: z
        .date({
          message: t('required_field'),
        })
        .superRefine((issueDate, ctx) => {
          // normalize dates to ignore the time part when comparing
          const formattedIssueDate = formatDateToString(issueDate)
          const formattedFiscalLockingDate = formatDateToString(fiscalLockingDate)

          if (formattedFiscalLockingDate && formattedIssueDate < formattedFiscalLockingDate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('external_invoices.editor.form.field.issue_date.error.min.fiscal_locking_date', {
                date: DateTimeFormat.getFormattedDate(fiscalLockingDate, getCurrentLocale()),
              }),
            })
            return
          }

          const formattedLatestInvoiceIssueDate = formatDateToString(latestInvoiceIssueDate)

          if (formattedLatestInvoiceIssueDate && formattedIssueDate < formattedLatestInvoiceIssueDate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('external_invoices.editor.form.field.issue_date.error.min.latest_issued_invoice', {
                date: DateTimeFormat.getFormattedDate(latestInvoiceIssueDate, getCurrentLocale()),
              }),
            })
          }
        }),
      language: z.nativeEnum(ZervantLocale),
      message: z.string().max(INVOICE_MESSAGE_CHARACTER_LIMIT),
      originalInvoiceTotalIncludingVat: z.number().optional(),
      paymentTerm: z.number(),
      purchaseOrderNumber: z.string().max(INVOICE_CLIENT_REFERENCE_CHARACTER_LIMIT).optional(),
      receiver: z.object(
        {
          cityText: z.string(),
          contactNo: z.string(),
          countryId: z.string(),
          ean: z.string(),
          externalId: z.string(),
          id: z.string(),
          name: z.string(),
          paymentTermsDays: z.number(),
          phone: z.string(),
          registrationNo: z.string(),
          street: z.string(),
          type: z.nativeEnum(ContactType),
          vatNo: z.string(),
          zipcodeText: z.string(),
        },
        {
          message: t('external_invoices.editor.form.field.send_to.error.required'),
        },
      ),
    })
    .superRefine((data, ctx) => {
      const hasRowLines = data.invoiceLines.some(isInvoiceFormRowLine)

      if (data.discountType === InvoiceDiscountType.Amount) {
        for (const [index, line] of data.invoiceLines.entries()) {
          if (!isInvoiceFormRowLine(line)) {
            continue
          }
          const maxDiscount = getTotalAmount(line.unitPrice, line.quantity)
          if (line.discount > maxDiscount) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: '',
              path: ['invoiceLines', index, 'discount'],
            })
          }
        }

        // A deposit-only invoice has no row lines a global discount could apply to - the carried
        // discount amount from the source document has nothing to do with the deposit's own
        // (much smaller) netTotal, so comparing them here would be a false positive.
        if (hasRowLines) {
          const { netTotal } = getInvoiceSummaryTotals({
            currency: data.currency,
            discountType: data.discountType,
            invoiceLines: data.invoiceLines,
          })
          if (data.discount > Math.abs(netTotal)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: '',
              path: ['discount'],
            })
          }
        }
      }

      if (!isCreditNote || data.originalInvoiceTotalIncludingVat === undefined) {
        return
      }

      // Line totals never bake in a global discount (see useUpdateDependentLineValues), so a raw
      // sum of totalIncludingVat would overstate the credit note total whenever one is carried over.
      const hasLineDiscount = hasInvoiceLineDiscount(data.invoiceLines)
      const { grossTotal } = getInvoiceSummaryTotals({
        currency: data.currency,
        discountType: data.discountType,
        globalDiscount: !hasLineDiscount && data.discount > 0 ? data.discount : undefined,
        invoiceLines: data.invoiceLines,
        isCreditNote,
      })
      const creditNoteGrossTotal = Math.abs(grossTotal)

      if (creditNoteGrossTotal > data.originalInvoiceTotalIncludingVat) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('external_invoices.editor.form.error.cn_total_exceeds_invoice'),
          path: ['root'],
        })
      }
    })
}
